import { useEffect, useState } from 'react'
import { ErrorNote } from './ui'
import { note } from './track'
import { here } from './shareLink'
import { useSettings } from '../settings'
import { BASE } from '../api'

/** What the form collects; the page adds the rest of its request. `prepared_for` is the person's full name. */
export interface ReportFields { /** the page's own address and query, linked from the PDF's footer */ link?: string; title?: string; prepared_for?: string; prepared_by?: string; notes?: string; email?: string; subscribe?: boolean; purpose?: string; purpose_note?: string }
const PURPOSES: [string, string][] = [['proposal', 'A client proposal'], ['decision', 'An internal decision'], ['looking', 'Just looking'], ['other', 'Something else']]

// who is exporting, kept for the visit so a second report does not ask again (in memory only, never stored)
const who = { name: '', email: '', join: false, by: (() => { try { return localStorage.getItem('prepared-by') ?? '' } catch { return '' } })() }
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/

/** Export: a round button with the download sign only (its name is its tooltip), then a small form, then the
 *  download. The title starts as the suggested one and can be changed.
 *  Where the site asks who the report is for, a full name and an email address are needed; the report is then
 *  kept, and listed for the administrator. `build` sends the page's request to the server, which prices it again
 *  and lays the document out. */
export function ExportPdf({ build, buildXlsx, placeholder, summary, disabled, label = 'Export', plain = false }: {
  build: (fields: ReportFields) => Promise<{ blob: Blob; filename: string }>
  /** the same report as a spreadsheet, where the page offers one: a second button on the form */
  buildXlsx?: (fields: ReportFields) => Promise<{ blob: Blob; filename: string }>
  /** the suggested title */
  placeholder?: string
  summary: string
  /** why there is nothing to export yet: the button stays, greyed, and says so */
  disabled?: string
  /** the trigger's name (its tooltip; its words where it is a plain row inside a menu, not the round button) */
  label?: string
  plain?: boolean
}) {
  const settings = useSettings()
  const asking = (settings.export_email ?? 'off') !== 'off'
  const required = settings.export_email === 'required'
  const newsletter = settings.newsletter === true
  const [open, setOpen] = useState(false)
  const suggested = placeholder ?? ''
  const [title, setTitle] = useState(suggested)
  const [edited, setEdited] = useState(false)
  // the suggestion follows the page (another model, another title) until the visitor writes their own
  useEffect(() => { if (!edited) setTitle(suggested) }, [suggested, edited])
  const [name, setName] = useState(who.name)
  const [email, setEmail] = useState(who.email)
  const [by, setBy] = useState(who.by)
  const [purpose, setPurpose] = useState('')
  const [purposeNote, setPurposeNote] = useState('')
  const [join, setJoin] = useState(who.join)
  const [notes, setNotes] = useState('')
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const nameOk = name.trim().replace(/\s+/g, ' ').length >= 2
  const emailOk = EMAIL.test(email.trim())
  const ready = !required || (nameOk && emailOk)
  const [kind, setKind] = useState<'pdf' | 'xlsx'>('pdf')
  const go = async (as: 'pdf' | 'xlsx' = 'pdf') => {
    setTried(true)
    if (!ready) return
    setBusy(true); setError(null); setKind(as)
    try {
      Object.assign(who, { name: name.trim(), email: email.trim(), join, by: by.trim() })
      try { localStorage.setItem('prepared-by', by.trim()) } catch { /* storage blocked */ }
      const make = as === 'xlsx' && buildXlsx ? buildXlsx : build
      const { blob, filename } = await make({
        title: title.trim() || suggested || undefined, prepared_for: name.trim() || undefined, prepared_by: by.trim() || undefined, notes: notes.trim() || undefined,
        email: asking ? email.trim() : undefined, subscribe: asking && newsletter && join,
        purpose: purpose || undefined, purpose_note: purposeNote.trim() || undefined, link: here(),
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; a.click()
      // the server counted the export when it kept it (exports.save): Umami alone is told here, with what it was
      note(as === 'xlsx' ? 'export_xlsx' : 'export_pdf', filename.split(/[-.]/)[0], { kind: filename.split(/[-.]/)[0], model: (title.trim() || suggested || '').slice(0, 80) })
      if (asking && newsletter && join && emailOk) note('newsletter_signup', 'export')
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      setOpen(false); setTried(false)
    } catch (e) { setError(e) } finally { setBusy(false) }
  }
  return (
    <span className="export">
      <button type="button" className={plain ? 'menurow' : 'xcircle'} onClick={() => setOpen(!open)} aria-expanded={open}
        disabled={Boolean(disabled)} title={disabled ?? (plain ? undefined : `${label} as ${buildXlsx ? 'PDF or Excel' : 'PDF'}`)} aria-label={plain ? undefined : `${label} as ${buildXlsx ? 'PDF or Excel' : 'PDF'}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
        </svg>
        {plain ? label : null}
      </button>
      {open ? (
        <form className="exportpop" role="dialog" aria-label={buildXlsx ? 'Export as PDF or Excel' : 'Export as PDF'} noValidate onSubmit={(e) => { e.preventDefault(); void go('pdf') }}>
          <div className="field"><label htmlFor="xt">Title</label>
            <input id="xt" value={title} maxLength={120} onChange={(e) => { setTitle(e.target.value); setEdited(true) }} />
            {edited && title.trim() !== suggested ? (
              <button type="button" className="linklike" onClick={() => { setEdited(false); setTitle(suggested) }}>use the suggested title</button>
            ) : null}
          </div>
          <div className="pair">
            <div className="field"><label htmlFor="xw">{required ? <>Your full name <b className="req" aria-hidden="true">*</b></> : asking ? <>Your name <span className="muted">(optional)</span></> : 'Prepared for'}</label>
              <input id="xw" value={name} maxLength={120} autoComplete="name" required={required} aria-invalid={required && tried && !nameOk}
                onChange={(e) => setName(e.target.value)} placeholder={asking ? 'First and last name' : 'Customer or team name'} />
              {required && tried && !nameOk ? <span className="fielderr">Enter your full name</span> : null}
            </div>
            {asking ? (
              <div className="field"><label htmlFor="xe">{required ? <>Your email <b className="req" aria-hidden="true">*</b></> : <>Your email <span className="muted">(optional)</span></>}</label>
                <input id="xe" type="email" autoComplete="email" required={required} value={email} maxLength={254} aria-invalid={required && tried && !emailOk}
                  onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
                {required && tried && !emailOk ? <span className="fielderr">Enter an email address, like name@company.com</span> : null}
              </div>
            ) : null}
          </div>
          <div className="field"><span className="muted small">What is this report for? <span className="muted">(optional, helps us build the right things)</span></span>
            <div className="chips" role="group" aria-label="What is this report for">
              {PURPOSES.map(([id, label]) => <button key={id} type="button" aria-pressed={purpose === id} onClick={() => setPurpose(purpose === id ? '' : id)}>{label}</button>)}
            </div>
            {purpose ? <input type="text" value={purposeNote} maxLength={1000} placeholder="A line about it, if you like" onChange={(e) => setPurposeNote(e.target.value)} style={{ marginTop: 6 }} /> : null}
          </div>
          <div className="field"><label htmlFor="xb">Prepared by <span className="muted">(optional) — your name and firm, printed as the author</span></label>
            <input id="xb" value={by} maxLength={160} autoComplete="organization" onChange={(e) => setBy(e.target.value)} placeholder="Sam Rivera, Acme Advisory" /></div>
          <div className="field"><label htmlFor="xn">Notes <span className="muted">(optional)</span></label>
            <textarea id="xn" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the reader should know: scope, caveats, next steps…" /></div>
          {asking && newsletter ? (
            <label className="check small exportjoin">
              <input type="checkbox" checked={join} onChange={(e) => setJoin(e.target.checked)} />
              Also send me the weekly newsletter: price moves, new models, retirements. One click to leave.
            </label>
          ) : null}
          <p className="muted small">
            {summary}{asking ? <> {required ? 'Your name is printed on the report. We keep a copy of the report with your name and email'
              : 'We keep a copy of every exported report. If you give your name and email, your name is printed on the report and both are kept with the copy'} so we can follow up on
              it; never shared or sold. See the <a href={`${BASE}privacy`} target="_blank" rel="noreferrer">privacy policy</a>.</>
              : <> We keep a copy of every exported report; never shared or sold. See the <a href={`${BASE}privacy`} target="_blank" rel="noreferrer">privacy policy</a>.</>}
          </p>
          <ErrorNote error={error} />
          <div className="row">
            <button type="submit" className="primary" disabled={busy}>{busy && kind === 'pdf' ? 'building…' : 'Download PDF'}</button>
            {buildXlsx ? <button type="button" onClick={() => void go('xlsx')} disabled={busy} title="The same figures as a spreadsheet: one sheet per section, numbers as numbers">{busy && kind === 'xlsx' ? 'building…' : 'Download Excel'}</button> : null}
            <button type="button" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          </div>
        </form>
      ) : null}
    </span>
  )
}
